// 유입 경로·광고 구분 (브라우저·서버 공용)
// 방문의 첫 페이지 주소(UTM·광고 클릭 표시)와 들어온 사이트로 "어느 광고·검색에서 왔는지"를 나눈다

export const CHANNELS = [
  'naver_ad',
  'naver',
  'youtube_ad',
  'youtube',
  'google_ad',
  'google',
  'daum',
  'kakao',
  'instagram',
  'facebook',
  'blog',
  'other',
  'direct',
  'internal',
] as const;
export type Channel = (typeof CHANNELS)[number];
export const isChannel = (v: unknown): v is Channel => (CHANNELS as readonly unknown[]).includes(v);

export const AD_CHANNELS = ['naver_ad', 'youtube_ad', 'google_ad'] as const;
export type AdChannel = (typeof AD_CHANNELS)[number];
export const isAdChannel = (v: unknown): v is AdChannel => (AD_CHANNELS as readonly unknown[]).includes(v);

export const CHANNEL_LABEL: Record<string, string> = {
  naver_ad: '네이버 광고',
  naver: '네이버 검색·자연 유입',
  youtube_ad: '유튜브 광고',
  youtube: '유튜브 (광고 표시 없음)',
  google_ad: '구글 광고 (유튜브 앱 포함)',
  google: '구글 검색',
  daum: '다음',
  kakao: '카카오',
  instagram: '인스타그램',
  facebook: '페이스북',
  blog: '블로그',
  other: '기타 사이트',
  direct: '직접 접속·앱',
  internal: '사이트 안 이동',
  unknown: '알 수 없음 (예전 신청)',
};

// 광고 주소에 붙인 캠페인 이름 → 관리자 화면에 보일 이름 (없으면 캠페인 이름 그대로)
export const CAMPAIGN_LABEL: Record<string, string> = {
  shorts_buy: '쇼츠① 번호판 구매',
  shorts_sell: '쇼츠② 번호판 판매·서류',
};

const PAID_MEDIUM = /^(cpc|ppc|paid|ad|ads|sa|powerlink|video|cpv|display|banner|shorts)$/i;

// 들어온 사이트 → 기본 분류 (예전 접속 통계의 source 와 같은 규칙)
export function sourceOf(host: string | null, ownHost: string, utmSource: string | null) {
  const utm = utmSource?.toLowerCase() ?? '';
  if (utm) {
    for (const k of ['naver', 'google', 'daum', 'kakao', 'instagram', 'youtube', 'facebook']) if (utm.includes(k)) return k;
    if (utm.includes('blog')) return 'blog';
  }
  if (!host) return 'direct';
  if (host === ownHost) return 'internal';
  if (/blog\.naver\.com|tistory\.com|brunch\.co\.kr|blog\.daum\.net/.test(host)) return 'blog';
  if (/naver\.com|naver\.me/.test(host)) return 'naver';
  if (/google\./.test(host)) return 'google';
  if (/daum\.net/.test(host)) return 'daum';
  if (/kakao/.test(host)) return 'kakao';
  if (/instagram\.com/.test(host)) return 'instagram';
  if (/youtube\.com|youtu\.be/.test(host)) return 'youtube';
  if (/facebook\.com|fb\.com/.test(host)) return 'facebook';
  return 'other';
}

export type Touch = {
  channel: Channel;
  source: string;
  referrer_host: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  keyword: string | null; // 네이버 광고 키워드 또는 utm_term
  click_id: 'gclid' | 'gbraid' | 'wbraid' | 'naver' | null;
  landing: string;
  at: string; // ISO
};

const clip = (v: string | null | undefined, max = 100) => (v && v.trim() ? v.trim().slice(0, max) : null);

// 첫 페이지 주소의 검색어(?...)와 들어온 사이트로 유입을 나눈다
export function classifyTouch(input: { search: string; referrer: string | null; ownHost: string; landing: string; at?: string }): Touch {
  const p = new URLSearchParams(input.search);
  let referrerHost: string | null = null;
  if (input.referrer) {
    try {
      referrerHost = new URL(input.referrer).hostname.toLowerCase();
    } catch {
      referrerHost = null;
    }
  }
  const utm_source = clip(p.get('utm_source'));
  const utm_medium = clip(p.get('utm_medium'));
  const source = sourceOf(referrerHost, input.ownHost, utm_source);

  // 네이버 검색광고: "자동 추적 URL" 을 켜면 NaPm·n_media·n_query·n_keyword 등이 붙는다
  const naverParams = ['NaPm', 'n_media', 'n_query', 'n_keyword', 'n_ad', 'n_ad_group', 'n_campaign_type', 'n_rank'];
  const naverAd =
    naverParams.some((k) => p.has(k)) ||
    /(^|\.)ad\.search\.naver\.com$/.test(referrerHost ?? '') ||
    (source === 'naver' && !!utm_medium && PAID_MEDIUM.test(utm_medium));
  // 구글·유튜브 광고: 자동 태그(gclid, 아이폰은 gbraid·wbraid)
  const googleClick = p.has('gclid') ? 'gclid' : p.has('gbraid') ? 'gbraid' : p.has('wbraid') ? 'wbraid' : null;
  const paidUtm = !!utm_medium && PAID_MEDIUM.test(utm_medium);

  let channel: Channel;
  if (naverAd) channel = 'naver_ad';
  else if (source === 'youtube' && (googleClick || paidUtm)) channel = 'youtube_ad';
  else if (googleClick || (source === 'google' && paidUtm)) channel = 'google_ad';
  else channel = isChannel(source) ? source : 'other';

  return {
    channel,
    source,
    referrer_host: source === 'internal' ? null : referrerHost,
    utm_source,
    utm_medium,
    utm_campaign: clip(p.get('utm_campaign')),
    utm_term: clip(p.get('utm_term')),
    utm_content: clip(p.get('utm_content')),
    keyword: clip(p.get('n_keyword') ?? p.get('n_query') ?? p.get('utm_term')),
    click_id: naverAd && naverParams.some((k) => p.has(k)) ? 'naver' : googleClick,
    landing: input.landing.slice(0, 300),
    at: input.at ?? new Date().toISOString(),
  };
}

// 상담 신청에 붙는 유입 정보 (payload.attribution)
export type LeadAttribution = { first: Touch | null; last: Touch | null; visitor_id: string | null; session_id: string | null };

function cleanTouch(v: unknown): Touch | null {
  if (!v || typeof v !== 'object') return null;
  const t = v as Record<string, unknown>;
  const s = (k: string, max = 100) => (typeof t[k] === 'string' ? clip(t[k] as string, max) : null);
  const click = s('click_id');
  return {
    channel: isChannel(t.channel) ? t.channel : 'other',
    source: s('source', 30) ?? 'other',
    referrer_host: s('referrer_host', 200),
    utm_source: s('utm_source'),
    utm_medium: s('utm_medium'),
    utm_campaign: s('utm_campaign'),
    utm_term: s('utm_term'),
    utm_content: s('utm_content'),
    keyword: s('keyword'),
    click_id: click === 'gclid' || click === 'gbraid' || click === 'wbraid' || click === 'naver' ? click : null,
    landing: s('landing', 300) ?? '/',
    at: s('at', 40) ?? '',
  };
}

// 서버: 브라우저가 보낸 유입 정보를 정리 (모양이 이상하면 버린다)
export function cleanAttribution(v: unknown): LeadAttribution | null {
  if (!v || typeof v !== 'object') return null;
  const a = v as Record<string, unknown>;
  const first = cleanTouch(a.first);
  const last = cleanTouch(a.last);
  if (!first && !last) return null;
  const id = (x: unknown) => (typeof x === 'string' ? x.slice(0, 64) : null);
  return { first, last, visitor_id: id(a.visitor_id), session_id: id(a.session_id) };
}

// 신청을 어느 채널 성과로 셀지: 마지막으로 광고·검색 등에서 들어온 경로 (없으면 첫 경로)
export const leadTouch = (a: LeadAttribution | null | undefined) => a?.last ?? a?.first ?? null;

export function touchSummary(t: Touch | null) {
  if (!t) return '';
  const parts = [CHANNEL_LABEL[t.channel] ?? t.channel, t.utm_campaign, t.keyword ? `키워드 "${t.keyword}"` : null];
  return parts.filter(Boolean).join(' · ');
}
